// OS DIÁLOGOS DA RELAÇÃO INSTITUCIONAL — criar, gravar a derivada, e alterar.
//
// Moram num arquivo próprio porque DUAS telas os abrem: a lista
// (`relacoes.tsx`) e a aba Relações da ficha (`organizacao-ficha.tsx`). Duas
// cópias divergiriam na primeira correção feita só de um lado — foi assim que o
// modal de cadastro de órgão acabou saindo para `organizacao-modal.tsx`.
//
// DOIS DIÁLOGOS, E POR QUE NÃO SÃO UM SÓ:
//
//   `NovaRelacaoModal`     um formulário — seis campos e validação por campo.
//   `AcaoRelacaoModal`     encerrar, suspender, reativar e corrigir. São o mesmo
//                          `PATCH` com justificativa obrigatória, e separá-los
//                          em quatro arquivos repetiria quatro vezes o mesmo
//                          tratamento de erro.
//
// A VALIDAÇÃO DA TELA USA AS MENSAGENS DO SERVIDOR (`lib/relacao-erros.ts`).
// Não é economia: é a garantia de que "responsável = atendida" diz a mesma coisa
// tendo sido o formulário ou a API a recusar. Duas redações para a mesma regra
// fazem o operador achar que são dois problemas.
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button, Modal, SelectField, TextField, TextareaField } from "@atlas/ui/web";
import { atlas } from "../../lib/sdk";
import { matchAnyKeys } from "../../lib/text-search";
import { mensagemDeErro } from "../../lib/erro-validacao";
import {
  lerConfirmacaoNecessaria,
  recusaDaRelacao,
  recusaDoErro,
  type ConfirmacaoDeEncerramento,
  type RecusaDaRelacao,
} from "../../lib/relacao-erros";
import {
  PAPEL_OPCOES,
  competenciaCorrente,
  competenciaValida,
  descricaoDoPapel,
  fmtCompetencia,
  fraseDaRelacao,
  rotuloDoPapel,
} from "./relacao-campos";
import type { Papel } from "@atlas/types";
import type { AdminPrefeitura, RelacaoInstitucionalDto } from "@atlas/sdk";

/** Toda gravação de relação invalida as duas leituras: a lista e as duas
 *  listas da ficha. O prefixo `["admin", "relacoes"]` cobre as duas. */
function useInvalidarRelacoes() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ["admin", "relacoes"] });
    // A lista de órgãos mostra "processada por": ela muda junto.
    void qc.invalidateQueries({ queryKey: ["admin", "prefeituras"] });
  };
}

const MIN_JUSTIFICATIVA = 3;
const HINT_JUSTIFICATIVA = "3 a 500 caracteres. Fica no histórico e na auditoria.";

type ResultadoCriacao =
  | {
      papel: Papel;
      status: "criada";
      relacao: RelacaoInstitucionalDto;
      materializadas: RelacaoInstitucionalDto[];
    }
  | {
      papel: Papel;
      status: "falhou";
      recusa: RecusaDaRelacao | null;
      mensagem: string;
    };

type FalhaCriacao = Extract<ResultadoCriacao, { status: "falhou" }>;

/** O bloco vermelho do topo, com o caminho até a relação que impede quando ela
 *  existe no banco. `relacaoId` nulo = a que impede é derivada, e não tem ficha. */
function BlocoRecusa({ recusa, atendidaId }: { recusa: RecusaDaRelacao; atendidaId?: number }) {
  return (
    <div
      role="alert"
      style={{
        border: "1px solid var(--danger-500)",
        borderRadius: 8,
        padding: "10px 12px",
        fontSize: 13,
        display: "grid",
        gap: 6,
      }}
    >
      <span>{recusa.texto}</span>
      {recusa.relacaoId != null && atendidaId != null ? (
        <Link to={`/averbadora/organizacoes/${atendidaId}?aba=relacoes`}>
          Ver as relações desta organização
        </Link>
      ) : null}
    </div>
  );
}

// ============================================================ o seletor de ponta

/**
 * Escolher uma organização entre duzentas.
 *
 * DOIS CONTROLES, e o de cima é o que faz o de baixo ser usável: um `<select>`
 * com a base inteira obriga a rolar até achar a autarquia, e o teclado do
 * navegador só casa o começo do NOME — quem procura por sigla ("A.M.T") não
 * acha nada. A busca varre nome, nome de exibição, sigla e CNPJ.
 *
 * A ESCOLHA NUNCA SOME DA LISTA. Filtrar a opção já escolhida faria o valor
 * gravado desaparecer da tela sem ter mudado — o operador veria outro nome
 * selecionado e não saberia por quê.
 */
function SeletorOrganizacao({
  rotulo,
  valor,
  aoEscolher,
  organizacoes,
  erro,
  id,
}: {
  rotulo: string;
  valor: number | "";
  aoEscolher: (id: number | "") => void;
  organizacoes: AdminPrefeitura[];
  erro?: string;
  id: string;
}) {
  const [busca, setBusca] = useState("");
  const visiveis = useMemo(() => {
    const casa = (o: AdminPrefeitura) =>
      matchAnyKeys(o, busca, ["nome", "nomeExibicao", "sigla", "cnpj", "razaoSocial"]);
    return organizacoes
      .filter((o) => casa(o) || o.id === valor)
      .slice()
      .sort((a, b) => (a.nomeExibicao ?? a.nome).localeCompare(b.nomeExibicao ?? b.nome, "pt-BR"));
  }, [organizacoes, busca, valor]);

  const rotuloOpcao = (o: AdminPrefeitura) => {
    const partes = [o.nomeExibicao ?? o.nome];
    if (o.sigla) partes.push(`(${o.sigla})`);
    if (o.municipioNome) partes.push(`· ${o.municipioNome}${o.uf ? `/${o.uf}` : ""}`);
    if (o.status === "inativo") partes.push("— inativa");
    else if (o.status === "pendente") partes.push("— cadastro pendente");
    return partes.join(" ");
  };

  return (
    <div style={{ display: "grid", gap: 8 }}>
      <TextField
        label={`Buscar ${rotulo.toLocaleLowerCase("pt-BR")}`}
        value={busca}
        onChange={(e) => setBusca(e.target.value)}
        placeholder="nome, sigla ou CNPJ"
      />
      <SelectField
        id={id}
        label={rotulo}
        required
        value={valor === "" ? "" : String(valor)}
        error={erro}
        onChange={(e) => aoEscolher(e.target.value === "" ? "" : Number(e.target.value))}
        hint={
          busca && visiveis.length !== organizacoes.length
            ? `${visiveis.length} de ${organizacoes.length} organizações`
            : undefined
        }
        options={[
          { value: "", label: "Escolha a organização…" },
          ...visiveis.map((o) => ({ value: String(o.id), label: rotuloOpcao(o) })),
        ]}
      />
    </div>
  );
}

// ============================================================ nova relação

export function NovaRelacaoModal({
  organizacoes,
  responsavelInicial,
  atendidaInicial,
  onClose,
}: {
  organizacoes: AdminPrefeitura[];
  /** Pré-preenchida quando o diálogo abre da ficha — editável, porque quem
   *  abriu pela ponta errada corrige aqui em vez de fechar e abrir de novo. */
  responsavelInicial?: number;
  atendidaInicial?: number;
  onClose: () => void;
}) {
  const invalidar = useInvalidarRelacoes();
  const [responsavelId, setResponsavelId] = useState<number | "">(responsavelInicial ?? "");
  const [atendidaId, setAtendidaId] = useState<number | "">(atendidaInicial ?? "");
  const [papeis, setPapeis] = useState<Papel[]>([]);
  const [competenciaInicio, setCompetenciaInicio] = useState(competenciaCorrente());
  const [competenciaFim, setCompetenciaFim] = useState("");
  const [justificativa, setJustificativa] = useState("");
  const [falhas, setFalhas] = useState<FalhaCriacao[]>([]);
  const [resultadoParcial, setResultadoParcial] = useState<ResultadoCriacao[] | null>(null);
  /** O que a gravação escreveu ALÉM do pedido — ver `criar` abaixo. */
  const [materializadas, setMaterializadas] = useState<RelacaoInstitucionalDto[] | null>(null);

  // A recusa da TELA usa as mensagens do servidor. A ordem é a mesma de
  // `validarRelacao`: primeiro o que está errado no que foi digitado.
  const recusaLocal = useMemo<RecusaDaRelacao | null>(() => {
    if (responsavelId !== "" && atendidaId !== "" && responsavelId === atendidaId) {
      return recusaDaRelacao({ codigo: "relacao_consigo_mesma" });
    }
    if (competenciaInicio && !competenciaValida(competenciaInicio)) {
      return recusaDaRelacao({ codigo: "competencia_invalida", campo: "competenciaInicio" });
    }
    if (competenciaFim && !competenciaValida(competenciaFim)) {
      return recusaDaRelacao({ codigo: "competencia_invalida", campo: "competenciaFim" });
    }
    if (competenciaFim && competenciaFim < competenciaInicio) {
      return recusaDaRelacao({ codigo: "fim_antes_do_inicio" });
    }
    return null;
  }, [responsavelId, atendidaId, competenciaInicio, competenciaFim]);

  const criar = useMutation({
    mutationFn: () =>
      Promise.all(
        papeis.map(async (papel): Promise<ResultadoCriacao> => {
          try {
            const resposta = await atlas.admin.relacoes.criar({
              responsavelId: responsavelId as number,
              atendidaId: atendidaId as number,
              papel,
              competenciaInicio,
              competenciaFim: competenciaFim || null,
              justificativa: justificativa.trim(),
            });
            return { papel, status: "criada", ...resposta };
          } catch (erro) {
            const recusa = recusaDoErro(erro);
            return {
              papel,
              status: "falhou",
              recusa,
              mensagem: recusa?.texto ?? mensagemDeErro(erro, "não consegui criar a relação"),
            };
          }
        }),
      ),
    onMutate: () => setFalhas([]),
    onSuccess: (resultados) => {
      const sucessos = resultados.filter((r) => r.status === "criada");
      const recusadas = resultados.filter((r): r is FalhaCriacao => r.status === "falhou");

      if (sucessos.length > 0) invalidar();
      if (recusadas.length > 0 && sucessos.length > 0) {
        setResultadoParcial(resultados);
        return;
      }
      if (recusadas.length > 0) {
        setFalhas(recusadas);
        return;
      }

      // GRAVAR SOBRE UM PAR DERIVADO ESCREVE OS TRÊS PAPÉIS, não só o pedido —
      // é como a API impede que materializar um revogue os outros dois em
      // silêncio. Fechar por cima disso esconderia o que acabou de acontecer.
      const materializadasNoLote = sucessos.flatMap((r) => r.materializadas);
      if (materializadasNoLote.length > 0) setMaterializadas(materializadasNoLote);
      else onClose();
    },
  });

  const recusaVisivel = recusaLocal;
  // UMA RECUSA, UM LUGAR. O conflito aparece no bloco do topo (é lá que está o
  // link para a relação que impede); repeti-lo sob o campo mostraria a mesma
  // frase duas vezes na mesma tela, e duas cópias parecem dois problemas.
  const noBloco = recusaVisivel != null && (recusaVisivel.campo === null || recusaVisivel.relacaoId != null);
  const erroDo = (campo: RecusaDaRelacao["campo"]) =>
    !noBloco && recusaVisivel?.campo === campo ? recusaVisivel.texto : undefined;

  const pronto =
    responsavelId !== "" &&
    atendidaId !== "" &&
    papeis.length > 0 &&
    competenciaValida(competenciaInicio) &&
    justificativa.trim().length >= MIN_JUSTIFICATIVA &&
    recusaLocal === null;

  const alternarPapel = (papel: Papel) => {
    setPapeis((atuais) =>
      atuais.includes(papel) ? atuais.filter((atual) => atual !== papel) : [...atuais, papel],
    );
    setFalhas([]);
  };

  if (resultadoParcial) {
    const materializadasNoLote = resultadoParcial.flatMap((r) =>
      r.status === "criada" ? r.materializadas : [],
    );
    return (
      <Modal
        open
        onClose={onClose}
        title="Relações parcialmente gravadas"
        footer={<Button onClick={onClose}>Entendi</Button>}
      >
        <div style={{ display: "grid", gap: 12, fontSize: 13 }}>
          <p style={{ margin: 0 }}>
            Algumas relações foram criadas e outras não. Confira o resultado de cada papel:
          </p>
          <ul style={{ margin: 0, paddingLeft: 18, display: "grid", gap: 10 }}>
            {resultadoParcial.map((resultado) => (
              <li key={resultado.papel}>
                {resultado.status === "criada" ? (
                  <span>
                    <b>{rotuloDoPapel(resultado.papel)}:</b> criada.
                  </span>
                ) : (
                  <div role="alert" style={{ display: "grid", gap: 4, color: "var(--danger-500)" }}>
                    <span>
                      <b>{rotuloDoPapel(resultado.papel)}:</b> não criada. {resultado.mensagem}
                    </span>
                    {resultado.recusa?.relacaoId != null && atendidaId !== "" ? (
                      <Link to={`/averbadora/organizacoes/${atendidaId}?aba=relacoes`}>
                        Ver as relações desta organização
                      </Link>
                    ) : null}
                  </div>
                )}
              </li>
            ))}
          </ul>
          {materializadasNoLote.length > 0 ? (
            <div style={{ display: "grid", gap: 6 }}>
              <b>Relações equivalentes também materializadas:</b>
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {materializadasNoLote.map((m) => (
                  <li key={`${m.id}`}>{fraseDaRelacao(m)}</li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      </Modal>
    );
  }

  if (materializadas) {
    return (
      <Modal
        open
        onClose={onClose}
        title="Relação gravada"
        footer={<Button onClick={onClose}>Entendi</Button>}
      >
        <div style={{ display: "grid", gap: 10, fontSize: 13 }}>
          <p style={{ margin: 0 }}>
            Este par já operava pelo vínculo antigo. Gravar registrou{" "}
            <b>as {materializadas.length} relações equivalentes</b>, e não só a que você pediu — assim que uma
            existe gravada, o vínculo antigo deixa de valer para o par inteiro, e escrever só uma revogaria
            as outras em silêncio.
          </p>
          <ul style={{ margin: 0, paddingLeft: 18, display: "grid", gap: 4 }}>
            {materializadas.map((m) => (
              <li key={`${m.id}`}>{fraseDaRelacao(m)}</li>
            ))}
          </ul>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Nova relação institucional"
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button disabled={!pronto || criar.isPending} onClick={() => criar.mutate()}>
            {criar.isPending ? "Criando…" : papeis.length > 1 ? "Criar relações" : "Criar relação"}
          </Button>
        </>
      }
    >
      <div style={{ display: "grid", gap: 14 }}>
        <p style={{ margin: 0, fontSize: 13, color: "var(--text-muted)" }}>
          A relação <b>dá acesso; não move propriedade</b>. Servidores, contratos, convênios e folhas
          continuam da organização atendida, e nada é herdado por causa dela.
        </p>

        {/* O bloco do topo aparece quando a recusa NÃO é de um campo (não há
            onde ancorá-la), ou quando ela aponta para outra relação — aí o que
            resolve não está neste formulário, e sim na relação que impede. */}
        {noBloco && recusaVisivel ? (
          <BlocoRecusa recusa={recusaVisivel} atendidaId={atendidaId === "" ? undefined : atendidaId} />
        ) : null}

        <SeletorOrganizacao
          id="relacao-responsavel"
          rotulo="Organização responsável"
          valor={responsavelId}
          aoEscolher={setResponsavelId}
          organizacoes={organizacoes}
          erro={erroDo("responsavelId")}
        />
        <SeletorOrganizacao
          id="relacao-atendida"
          rotulo="Organização atendida"
          valor={atendidaId}
          aoEscolher={setAtendidaId}
          organizacoes={organizacoes}
          erro={erroDo("atendidaId")}
        />

        <fieldset
          aria-describedby="relacao-papeis-hint"
          style={{ margin: 0, padding: 12, border: "1px solid var(--border)", borderRadius: 8 }}
        >
          <legend style={{ padding: "0 4px", fontSize: 13, fontWeight: 600 }}>
            Papéis <span aria-hidden="true">*</span>
          </legend>
          <div
            id="relacao-papeis-hint"
            style={{ marginBottom: 10, fontSize: 12, color: "var(--text-muted)" }}
          >
            O que a responsável passa a fazer pela atendida. Marque um ou mais papéis.
          </div>
          <div style={{ display: "grid", gap: 10 }}>
            {PAPEL_OPCOES.map((opcao) => {
              const papel = opcao.value;
              const id = `relacao-papel-${papel}`;
              return (
                <label key={papel} htmlFor={id} style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                  <input
                    id={id}
                    type="checkbox"
                    checked={papeis.includes(papel)}
                    onChange={() => alternarPapel(papel)}
                    style={{ marginTop: 2 }}
                  />
                  <span style={{ display: "grid", gap: 2 }}>
                    <b style={{ fontSize: 13 }}>{opcao.label}</b>
                    <span style={{ fontSize: 12, color: "var(--text-muted)" }}>
                      {descricaoDoPapel(papel)}
                    </span>
                  </span>
                </label>
              );
            })}
          </div>
        </fieldset>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
          <TextField
            id="relacao-competencia-inicio"
            label="Competência inicial"
            required
            inputMode="numeric"
            value={competenciaInicio}
            error={erroDo("competenciaInicio")}
            hint="AAAAMM. A vigência é por competência da folha, não por data."
            onChange={(e) => setCompetenciaInicio(e.target.value.replace(/\D/g, "").slice(0, 6))}
          />
          <TextField
            id="relacao-competencia-fim"
            label="Competência final"
            inputMode="numeric"
            value={competenciaFim}
            error={erroDo("competenciaFim")}
            hint="Opcional. Em branco = sem fim previsto."
            onChange={(e) => setCompetenciaFim(e.target.value.replace(/\D/g, "").slice(0, 6))}
          />
        </div>

        <TextareaField
          id="relacao-justificativa"
          label="Justificativa"
          required
          value={justificativa}
          hint={HINT_JUSTIFICATIVA}
          onChange={(e) => setJustificativa(e.target.value)}
        />

        {falhas.length > 0 ? (
          <div
            role="alert"
            style={{
              border: "1px solid var(--danger-500)",
              borderRadius: 8,
              padding: "10px 12px",
              fontSize: 13,
            }}
          >
            <b>Nenhuma relação foi criada.</b>
            <ul style={{ margin: "8px 0 0", paddingLeft: 18, display: "grid", gap: 8 }}>
              {falhas.map((falha) => (
                <li key={falha.papel}>
                  <b>{rotuloDoPapel(falha.papel)}:</b> {falha.mensagem}
                  {falha.recusa?.relacaoId != null && atendidaId !== "" ? (
                    <div>
                      <Link to={`/averbadora/organizacoes/${atendidaId}?aba=relacoes`}>
                        Ver as relações desta organização
                      </Link>
                    </div>
                  ) : null}
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>
    </Modal>
  );
}

// `GravarDerivadaModal` SAIU NA FASE D (T060).
//
// Ele era uma confirmação, e não um formulário: a relação derivada do vínculo
// antigo já valia, e gravá-la não mudava acesso nenhum — o que precisava ser
// dito era que a gravação escrevia os TRÊS papéis do par, e não só o pedido.
// A migração da fase C gravou todas, a derivação saiu em T058, e um diálogo
// para um estado que a API não produz mais é um botão que ninguém alcança.

// ============================================================ alterar

export type AcaoRelacao = "encerrar" | "suspender" | "reativar" | "corrigir";

const TITULO_ACAO: Record<AcaoRelacao, string> = {
  encerrar: "Encerrar relação",
  suspender: "Suspender relação",
  reativar: "Reativar relação",
  corrigir: "Corrigir vigência",
};

/**
 * As quatro alterações — o mesmo `PATCH`, e a mesma justificativa obrigatória.
 *
 * NÃO HÁ EXCLUSÃO, e a ausência é o contrato: encerrar grava a competência
 * final. Apagar a linha destruiria a resposta para "quem respondia por esta
 * folha em março?", e a folha de março continua existindo depois que a relação
 * acaba.
 */
export function AcaoRelacaoModal({
  relacao,
  acao,
  onClose,
}: {
  relacao: RelacaoInstitucionalDto;
  acao: AcaoRelacao;
  onClose: () => void;
}) {
  const invalidar = useInvalidarRelacoes();
  const [competenciaFim, setCompetenciaFim] = useState(
    acao === "encerrar" ? competenciaCorrente() : (relacao.competenciaFim ?? ""),
  );
  const [competenciaInicio, setCompetenciaInicio] = useState(relacao.competenciaInicio);
  const [justificativa, setJustificativa] = useState("");
  const [recusa, setRecusa] = useState<RecusaDaRelacao | null>(null);
  const [erroGeral, setErroGeral] = useState<string | null>(null);
  const [confirmacao, setConfirmacao] = useState<ConfirmacaoDeEncerramento | null>(null);

  const patch = (confirmar: boolean) => {
    const base = { justificativa: justificativa.trim(), ...(confirmar ? { confirmar: true } : {}) };
    if (acao === "encerrar") return { ...base, competenciaFim };
    // O MOTIVO É A JUSTIFICATIVA. Pedir os dois separados seria pedir duas
    // vezes a mesma frase — o que a suspensão precisa registrar é por que o
    // acesso foi bloqueado agora, e é isso que está escrito no campo.
    if (acao === "suspender") return { ...base, suspender: { motivo: justificativa.trim() } };
    if (acao === "reativar") return { ...base, reativar: true as const };
    return { ...base, competenciaInicio, competenciaFim: competenciaFim || null };
  };

  const alterar = useMutation({
    mutationFn: (confirmar: boolean) => atlas.admin.relacoes.alterar(relacao.id as number, patch(confirmar)),
    onSuccess: () => {
      invalidar();
      onClose();
    },
    onError: (e: unknown) => {
      // A confirmação vem ANTES da recusa: `409` não é erro de preenchimento,
      // é a API pedindo que alguém veja a consequência antes de repetir.
      const c = lerConfirmacaoNecessaria(e);
      if (c) {
        setConfirmacao(c);
        setRecusa(null);
        setErroGeral(null);
        return;
      }
      const r = recusaDoErro(e);
      setRecusa(r);
      setErroGeral(r ? null : mensagemDeErro(e, "não consegui alterar a relação"));
    },
  });

  const recusaLocal = useMemo<RecusaDaRelacao | null>(() => {
    if (acao === "encerrar" && competenciaFim && !competenciaValida(competenciaFim)) {
      return recusaDaRelacao({ codigo: "competencia_invalida", campo: "competenciaFim" });
    }
    if (acao === "corrigir") {
      if (!competenciaValida(competenciaInicio)) {
        return recusaDaRelacao({ codigo: "competencia_invalida", campo: "competenciaInicio" });
      }
      if (competenciaFim && !competenciaValida(competenciaFim)) {
        return recusaDaRelacao({ codigo: "competencia_invalida", campo: "competenciaFim" });
      }
      if (competenciaFim && competenciaFim < competenciaInicio) {
        return recusaDaRelacao({ codigo: "fim_antes_do_inicio" });
      }
    }
    return null;
  }, [acao, competenciaInicio, competenciaFim]);

  const recusaVisivel = recusa ?? recusaLocal;
  const noBloco = recusaVisivel != null && (recusaVisivel.campo === null || recusaVisivel.relacaoId != null);
  const erroDo = (campo: RecusaDaRelacao["campo"]) =>
    !noBloco && recusaVisivel?.campo === campo ? recusaVisivel.texto : undefined;

  const precisaCompetencia = acao === "encerrar" || acao === "corrigir";
  // Só encerrar EXIGE a competência final: corrigir aceita apagá-la (é assim que
  // uma relação volta a não ter fim previsto), e as outras duas não a tocam.
  const pronto =
    justificativa.trim().length >= MIN_JUSTIFICATIVA &&
    recusaLocal === null &&
    (acao !== "encerrar" || competenciaValida(competenciaFim));

  const rotuloJustificativa = acao === "suspender" ? "Motivo da suspensão" : "Justificativa";

  return (
    <Modal
      open
      onClose={onClose}
      title={TITULO_ACAO[acao]}
      danger={acao === "encerrar"}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button disabled={!pronto || alterar.isPending} onClick={() => alterar.mutate(confirmacao != null)}>
            {alterar.isPending ? "Salvando…" : confirmacao ? "Encerrar mesmo assim" : TITULO_ACAO[acao]}
          </Button>
        </>
      }
    >
      <div style={{ display: "grid", gap: 14, fontSize: 13 }}>
        <p style={{ margin: 0 }}>
          <b>{fraseDaRelacao(relacao)}</b>
        </p>

        {acao === "encerrar" ? (
          <p style={{ margin: 0, color: "var(--text-muted)" }}>
            Encerrar não apaga: a relação continua no histórico, e as competências que ela cobriu continuam
            respondendo por quem as operou. A competência final é <b>inclusiva</b>.
          </p>
        ) : null}
        {acao === "suspender" ? (
          <p style={{ margin: 0, color: "var(--text-muted)" }}>
            Suspender bloqueia o acesso <b>agora</b>, sem mexer na vigência — é o que se usa enquanto se apura
            alguma coisa. Para terminar de vez, use Encerrar.
          </p>
        ) : null}

        {/* A CONFIRMAÇÃO, com as competências nomeadas. O efeito de encerrar
            quem processa a folha é invisível no clique e só aparece na virada do
            mês, quando ninguém mais liga uma coisa à outra. */}
        {confirmacao ? (
          <div
            role="alert"
            style={{
              border: "1px solid var(--gold-500)",
              borderRadius: 8,
              padding: "10px 12px",
              display: "grid",
              gap: 6,
            }}
          >
            <b>{confirmacao.mensagem}</b>
            {confirmacao.folhas.length > 0 ? (
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {confirmacao.folhas.map((f) => (
                  <li key={f.competencia}>
                    {fmtCompetencia(f.competencia)} — {f.status}
                  </li>
                ))}
              </ul>
            ) : null}
            <span style={{ color: "var(--text-muted)" }}>
              Ou informe uma competência final posterior, ou crie antes a relação de quem passa a processar.
            </span>
          </div>
        ) : null}

        {noBloco && recusaVisivel ? (
          <BlocoRecusa recusa={recusaVisivel} atendidaId={relacao.atendidaId} />
        ) : null}

        {acao === "corrigir" ? (
          <TextField
            id="relacao-corrigir-inicio"
            label="Competência inicial"
            required
            inputMode="numeric"
            value={competenciaInicio}
            error={erroDo("competenciaInicio")}
            hint="Adiar o início de uma relação já operada é recusado: a competência operada apontaria para ninguém."
            onChange={(e) => setCompetenciaInicio(e.target.value.replace(/\D/g, "").slice(0, 6))}
          />
        ) : null}

        {precisaCompetencia ? (
          <TextField
            id="relacao-competencia-fim"
            label="Competência final"
            required={acao === "encerrar"}
            inputMode="numeric"
            value={competenciaFim}
            error={erroDo("competenciaFim")}
            hint={
              acao === "encerrar"
                ? "AAAAMM, inclusiva — é o último mês que a relação cobre."
                : "Em branco = sem fim previsto."
            }
            onChange={(e) => setCompetenciaFim(e.target.value.replace(/\D/g, "").slice(0, 6))}
          />
        ) : null}

        <TextareaField
          id="relacao-acao-justificativa"
          label={rotuloJustificativa}
          required
          value={justificativa}
          hint={HINT_JUSTIFICATIVA}
          onChange={(e) => setJustificativa(e.target.value)}
        />

        {erroGeral ? (
          <div role="alert" style={{ color: "var(--danger-500)" }}>
            {erroGeral}
          </div>
        ) : null}
      </div>
    </Modal>
  );
}
